// ─── Types ───────────────────────────────────────────────

export interface ParsedFile {
    name: string;
    content: string;
    sizeBytes: number;
}

// ─── Constants ───────────────────────────────────────────

/** Maximum number of .md files that can be imported at once. */
export const MAX_IMPORT_FILES = 3000;

/** Maximum combined file size in bytes (~200 MB). */
export const MAX_IMPORT_SIZE_BYTES = 200 * 1024 * 1024;

// ─── Markdown → TipTap Conversion ───────────────────────

/**
 * Parse inline marks: **bold**, *italic*, `code`
 * Returns an array of TipTap text nodes with appropriate marks.
 */
export function parseInlineMarks(text: string): Record<string, unknown>[] {
    const result: Record<string, unknown>[] = [];

    // Order matters: bold (**) before italic (*), backtick for code
    const pattern = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(text)) !== null) {
        if (match.index > lastIndex) {
            const plainText = text.slice(lastIndex, match.index);
            if (plainText) {
                result.push({ type: "text", text: plainText });
            }
        }

        if (match[2]) {
            // Bold **text**
            result.push({
                type: "text",
                text: match[2],
                marks: [{ type: "bold" }],
            });
        } else if (match[3]) {
            // Italic *text*
            result.push({
                type: "text",
                text: match[3],
                marks: [{ type: "italic" }],
            });
        } else if (match[4]) {
            // Code `text`
            result.push({
                type: "text",
                text: match[4],
                marks: [{ type: "code" }],
            });
        }

        lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
        const remaining = text.slice(lastIndex);
        if (remaining) {
            result.push({ type: "text", text: remaining });
        }
    }

    if (result.length === 0 && text) {
        result.push({ type: "text", text });
    }

    return result;
}

/**
 * Converts markdown text into a TipTap-compatible JSON document.
 * Handles headings (# through ###), paragraphs, bold, italic, and inline code.
 */
export function markdownToTiptap(markdown: string): Record<string, unknown> {
    const lines = markdown.split("\n");
    const nodes: Record<string, unknown>[] = [];

    for (const line of lines) {
        const trimmed = line.trimEnd();

        const headingMatch = trimmed.match(/^(#{1,3})\s+(.+)$/);
        if (headingMatch) {
            const level = headingMatch[1].length;
            const text = headingMatch[2];
            nodes.push({
                type: "heading",
                attrs: { level },
                content: parseInlineMarks(text),
            });
            continue;
        }

        // Standalone image line: ![alt](url) or ![alt](url "title"). The src is
        // kept as-is (external hotlink) — re-hosting imported images to R2 is a
        // separate concern. Only http(s) srcs become figures; anything else
        // (javascript:, data:, relative, malformed) falls through to be rendered
        // as literal text rather than injected into an <img src>.
        const imageMatch = trimmed.match(
            /^!\[(.*?)\]\(\s*(\S+?)(?:\s+"([^"]*)")?\s*\)$/,
        );
        if (imageMatch && /^https?:\/\//i.test(imageMatch[2])) {
            const alt = imageMatch[1];
            const src = imageMatch[2];
            const title = imageMatch[3] || null;
            nodes.push({
                type: "figure",
                attrs: { src, alt: alt || null, title },
            });
            continue;
        }

        if (trimmed === "") {
            nodes.push({ type: "paragraph" });
            continue;
        }

        nodes.push({
            type: "paragraph",
            content: parseInlineMarks(trimmed),
        });
    }

    if (nodes.length === 0) {
        nodes.push({ type: "paragraph" });
    }

    return { type: "doc", content: nodes };
}

// ─── Metadata Extraction ────────────────────────────────

/**
 * Count words in markdown text, stripping formatting characters first.
 */
export function countWordsFromMarkdown(text: string): number {
    return text
        .replace(/[#*`_~\[\](){}|>]/g, " ")
        .trim()
        .split(/\s+/)
        .filter((w) => w.length > 0).length;
}

/**
 * Extract a chapter title from markdown content.
 * Looks for the first heading (H1–H3), falls back to filename.
 */
export function extractChapterTitle(
    content: string,
    filename: string,
): string {
    const headingMatch = content.match(/^#{1,3}\s+(.+)$/m);
    if (headingMatch) {
        return headingMatch[1].trim();
    }
    // Strip any source extension (.md, .docx, …), not just .md, so a heading-less
    // file doesn't carry its extension into the chapter title.
    return filename.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ");
}

// ─── Sorting ────────────────────────────────────────────

/**
 * Natural sort comparator — handles numeric ordering
 * (e.g. chapter-1, chapter-2, chapter-10).
 */
export function naturalSort(a: ParsedFile, b: ParsedFile): number {
    return a.name.localeCompare(b.name, undefined, {
        numeric: true,
        sensitivity: "base",
    });
}

// ─── File Processing ────────────────────────────────────

/**
 * Validate a set of parsed files against import limits.
 * Returns an error message string, or `null` if valid.
 */
export function validateImportFiles(files: ParsedFile[]): string | null {
    if (files.length === 0) {
        return "No .md files found. Please select a folder containing Markdown files.";
    }
    if (files.length > MAX_IMPORT_FILES) {
        return `Too many files (${files.length}). Maximum is ${MAX_IMPORT_FILES}.`;
    }
    const totalBytes = files.reduce((sum, f) => sum + f.sizeBytes, 0);
    if (totalBytes > MAX_IMPORT_SIZE_BYTES) {
        const sizeMb = (totalBytes / (1024 * 1024)).toFixed(1);
        const limitMb = (MAX_IMPORT_SIZE_BYTES / (1024 * 1024)).toFixed(0);
        return `Total file size (${sizeMb} MB) exceeds the ${limitMb} MB limit.`;
    }
    return null;
}

/**
 * Split a single markdown document into per-chapter ParsedFile chunks at every
 * heading of the given level (1 = `# `, 2 = `## `). Used when an author imports
 * one large document containing many chapters separated by headings, rather than
 * one file per chapter.
 *
 * - Each chunk RETAINS its heading line, so it flows through the same downstream
 *   pipeline (extractChapterTitle / markdownToTiptap / countWords) as a
 *   standalone file would.
 * - A deeper heading than `level` (e.g. `## ` while splitting on level 1) stays
 *   inside its parent chunk.
 * - Content appearing before the first matching heading becomes its own leading
 *   chunk.
 * - Whitespace-only chunks are dropped; empty input yields an empty array.
 */
export function splitMarkdownByHeadings(
    markdown: string,
    level: 1 | 2,
): ParsedFile[] {
    if (!markdown.trim()) return [];

    // Match a heading of EXACTLY `level` hashes: the char after the hashes must
    // be whitespace, so `## ` never matches a level-1 split and vice versa.
    const headingRe = new RegExp(`^#{${level}}\\s+(.+)$`);

    const chunks: { title: string | null; lines: string[] }[] = [];
    let current: { title: string | null; lines: string[] } | null = null;

    for (const line of markdown.split("\n")) {
        const match = line.trimEnd().match(headingRe);
        if (match) {
            current = { title: match[1].trim(), lines: [line] };
            chunks.push(current);
        } else if (current) {
            current.lines.push(line);
        } else {
            // Preamble before the first matching heading.
            current = { title: null, lines: [line] };
            chunks.push(current);
        }
    }

    return chunks
        .map((chunk, index) => ({
            title: chunk.title ?? `Section ${index + 1}`,
            content: chunk.lines.join("\n").trim(),
        }))
        .filter((chunk) => chunk.content.length > 0)
        .map((chunk) => ({
            name: `${chunk.title}.md`,
            content: chunk.content,
            sizeBytes: new TextEncoder().encode(chunk.content).length,
        }));
}

/**
 * Build chapter import payloads from a sorted array of parsed files.
 */
export function buildChaptersPayload(files: ParsedFile[], startIndex: number = 0) {
    return files.map((file, index) => ({
        title: extractChapterTitle(file.content, file.name),
        content: markdownToTiptap(file.content),
        wordCount: countWordsFromMarkdown(file.content),
        sortOrder: startIndex + index + 1,
    }));
}
